import type { ReactNode } from 'react';

export type SupplierKpiTone = 'teal' | 'success' | 'brand' | 'violet' | 'warning' | 'danger';

const TONE_MAP: Record<
  SupplierKpiTone,
  { gradient: string; iconBg: string; tintBg: string; darkTintBg: string }
> = {
  teal: {
    gradient: 'bg-gradient-to-br from-teal-500 to-teal-700',
    iconBg: 'bg-gradient-to-br from-teal-500 to-teal-600',
    tintBg: 'bg-teal-50',
    darkTintBg: 'dark:bg-teal-900/20',
  },
  success: {
    gradient: 'bg-gradient-to-br from-success-500 to-success-700',
    iconBg: 'bg-gradient-to-br from-success-500 to-success-600',
    tintBg: 'bg-success-50',
    darkTintBg: 'dark:bg-success-900/20',
  },
  brand: {
    gradient: 'bg-gradient-to-br from-brand-500 to-brand-700',
    iconBg: 'bg-gradient-to-br from-brand-500 to-brand-600',
    tintBg: 'bg-brand-50',
    darkTintBg: 'dark:bg-brand-900/20',
  },
  violet: {
    gradient: 'bg-gradient-to-br from-violet-500 to-violet-700',
    iconBg: 'bg-gradient-to-br from-violet-500 to-violet-600',
    tintBg: 'bg-violet-50',
    darkTintBg: 'dark:bg-violet-900/20',
  },
  warning: {
    gradient: 'bg-gradient-to-br from-warning-500 to-warning-700',
    iconBg: 'bg-gradient-to-br from-warning-500 to-warning-600',
    tintBg: 'bg-warning-50',
    darkTintBg: 'dark:bg-warning-900/20',
  },
  danger: {
    gradient: 'bg-gradient-to-br from-danger-500 to-danger-700',
    iconBg: 'bg-gradient-to-br from-danger-500 to-danger-600',
    tintBg: 'bg-danger-50',
    darkTintBg: 'dark:bg-danger-900/20',
  },
};

export interface SupplierKpiCardProps {
  title: string;
  value: string;
  icon: ReactNode;
  tone: SupplierKpiTone;
  hint?: string;
}

export function SupplierKpiCard({ title, value, icon, tone, hint }: SupplierKpiCardProps) {
  const t = TONE_MAP[tone];
  return (
    <div
      className={`relative overflow-hidden rounded-xl ${t.tintBg} ${t.darkTintBg} shadow-md hover:shadow-lg transition-all duration-200 p-5 animate-slide-up`}
    >
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${t.gradient} opacity-20`} />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${t.iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
        {hint && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{hint}</p>}
      </div>
    </div>
  );
}
